import { useContext, useMemo, useState } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";

import Layout from "src/components/Layout/Layout";
import PageHeader from "src/components/PageHeader/PageHeader";
import Table from "src/components/Table/Table";
import TrackedLink from "src/components/TrackedLink/TrackedLink";
import { ProfileContext } from "src/providers/profile-provider";
import { UserOrganization, useGetUserMergedOrganizations } from "src/hooks/useGetUserMergedOrganizations";
import { Lozenge } from "@k8slens/lds";
import clsx from "clsx";
import styles from "./page.module.css";

const columnHelper = createColumnHelper<UserOrganization>();

export const Organizations = () => {
  const { profile } = useContext(ProfileContext);
  const { loading, organizations } = useGetUserMergedOrganizations();
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => <span>Organization</span>,
        cell: ({ row }) => {
          const { id, name, role, state } = row.original;

          return (
            <>
              {role === "Administrator" && !state ? (
                <TrackedLink className="primary" to={`/business/${id}`}>
                  {name}
                </TrackedLink>
              ) : (
                name
              )}
              {state ? (
                <Lozenge type="primary" size="sm" className="ml-md uppercase">
                  {state}
                </Lozenge>
              ) : null}
            </>
          );
        },
      }),
      columnHelper.accessor("department", { header: () => <span>Department</span> }),
      columnHelper.accessor("license", { header: () => <span>Subscription</span> }),
      columnHelper.accessor("role", { header: () => <span>Role</span> }),
    ],
    [],
  );

  const table = useReactTable({
    data: organizations,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const rows = table.getRowModel().rows;

  return (
    <Layout className="bg-secondary" profile={profile}>
      <PageHeader
        title="Organizations"
        subtitle="Manage your organization memberships, respond to invites, join an existing organization, or create a new one to access shared features."
      />
      <h3 className={clsx("lds-form-label lds-form-field--label", styles.label)}>Your organizations</h3>
      <p className={clsx("lds-form-switch-group--description", styles.description)}>
        Here you can review, join, leave, and set your active
      </p>
      <Table
        caption="Organizations"
        rows={rows}
        columns={table.getAllColumns()}
        headerGroups={table.getHeaderGroups()}
        loading={loading}
      />
    </Layout>
  );
};
